import { type AutopilotTaskDto, useAssignTaskToUser } from '@urbanx/agx.activities.client';
import { AgencyModel } from '@urbanx/agx.agencies.client';
import {
  AgxBadge,
  AgxModal,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';
import TaskAgencyCell from 'pages/allTasks/Cells/TaskAgencyCell';
import TaskAssigneeCell from 'pages/allTasks/Cells/TaskAssigneeCell';
import TaskCampaignCell from 'pages/allTasks/Cells/TaskCampaignCell';
import { TaskCell } from 'pages/allTasks/Cells/TaskCell';
import TaskDueCell from 'pages/allTasks/Cells/TaskDueCell';
import TaskStatusCell from 'pages/allTasks/Cells/TaskStatusCell';
import useTaskSearch from 'pages/allTasks/useTaskSearch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigateToTask } from 'utils/tasks/useNavigateToTask';
import { useWorkflowConcurrencyError } from 'hooks/useWorkflowConcurrencyError';
import { CreateTaskFilterState } from './CreateTaskFilterState';
import { TasksSearchFilterType } from './TasksSearchFilterType';

interface TaskTableProps {
  assignedToUserIds?: string[];
  selectedAgencies?: string[];
  searchFilterType: TasksSearchFilterType;
  agencies: AgencyModel[];
  assigneeOptions: SelectChipOption[];
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
}

const tableColumns: (string | IColumnConfig)[] = [
  'Task',
  'Agency',
  'Campaign',
  { name: 'Assignee', align: 'center' },
  'Status',
  { name: 'Due', align: 'right' },
];

const TasksModal = ({
  assignedToUserIds,
  selectedAgencies,
  searchFilterType,
  agencies,
  assigneeOptions,
  isModalOpen,
  setIsModalOpen,
}: TaskTableProps) => {
  const taskFilterState = useMemo(
    () => CreateTaskFilterState(searchFilterType, assignedToUserIds, selectedAgencies),
    [searchFilterType, assignedToUserIds, selectedAgencies]
  );

  const { tasks, searchAllTasksPending, totalCount, hasMore, loadMore, refreshSearch } =
    useTaskSearch(taskFilterState, false);
  const assignmentWorkflowRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();
  const { assignTaskToUser, assignTaskToUserPending } = useAssignTaskToUser({
    onSuccess: () => {},
    onError: (error) => {
      if (!assignmentWorkflowRef.current) return;

      void handleWorkflowConcurrencyError(error, assignmentWorkflowRef.current, {
        refresh: refreshSearch,
      });
    },
  });

  const onAssignTaskToUser = useCallback(
    (userId?: string, taskId?: string) => {
      if (!userId || !taskId || assignTaskToUserPending) return;

      assignmentWorkflowRef.current = tasks.find((task) => task.id === taskId);

      assignTaskToUser({
        taskId,
        userId,
      });
    },
    [assignTaskToUserPending, assignTaskToUser, tasks]
  );

  const navigateToTask = useNavigateToTask();

  const onTaskRowClicked = useCallback(
    (rowIndex: number) => {
      if (!tasks || rowIndex < 0 || rowIndex >= tasks.length) return;

      const task = tasks[rowIndex];

      navigateToTask(task);
    },
    [tasks, navigateToTask]
  );

  const [lastRowElement, setLastRowElement] = useState<HTMLDivElement | null>(null);
  const [isLastRowVisible, setIsLastRowVisible] = useState(false);

  // Callback ref - fires when the element mounts/unmounts
  const lastRowRef = useCallback((node: HTMLDivElement | null) => {
    setLastRowElement(node);
  }, []);

  // Detect when the last row becomes visible in the scrollable container
  useEffect(() => {
    if (!lastRowElement || !isModalOpen) {
      refreshSearch();
      setIsLastRowVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsLastRowVisible(entries[0].isIntersecting);
      },
      {
        // Use null for viewport, or you can specify a scroll container root
        root: null,
        // Trigger 300px before the element becomes visible for smoother loading
        rootMargin: '0px 0px 300px 0px',
        // threshold: 0 = any part visible, 1.0 = fully visible
        threshold: 0,
      }
    );

    observer.observe(lastRowElement);

    return () => observer.disconnect();
  }, [isModalOpen, lastRowElement]);

  useEffect(() => {
    if (!isLastRowVisible) return;
    if (!hasMore) return;
    if (searchAllTasksPending) return;

    loadMore();
  }, [isLastRowVisible, hasMore, searchAllTasksPending]);

  const rows = useMemo(() => {
    return tasks.map((task, idx) => {
      const isNew = !!task.isNew;
      const isLastRow = idx === tasks.length - 1;

      return {
        Task: (
          <div ref={isLastRow ? lastRowRef : null}>
            <TaskCell task={task} index={idx} isNew={isNew} />
          </div>
        ),
        Agency: <TaskAgencyCell task={task} agencies={agencies ?? []} index={idx} isNew={isNew} />,
        Campaign: (
          <TaskCampaignCell
            propertyAddress={task.propertyAddress ?? 'Unknown Address'}
            index={idx}
            isNew={isNew}
          />
        ),
        Assignee: (
          <div onClick={(e) => e.stopPropagation()}>
            <TaskAssigneeCell
              task={task}
              assigneeOptions={assigneeOptions}
              onAssignTask={onAssignTaskToUser}
            />
          </div>
        ),
        Status: <TaskStatusCell task={task} />,
        Due: <TaskDueCell task={task} />,
      };
    });
  }, [searchAllTasksPending, tasks, agencies, assigneeOptions]);

  return (
    <AgxModal
      type={'fullWidth'}
      variant={'withoutDivider'}
      actionButtonContainerStyle={'withDivider'}
      secondaryTitle={
        <AgxSpacer direction="horizontal" gap={'2xs'}>
          <AgxTypography
            id="agx-modal-title"
            size="medium"
            type="body_ui_strong"
            dataTestId={`agx-modal-${searchFilterType.toString()}`}
          >
            {`Today's ${searchFilterType.toString()}`}
          </AgxTypography>
          <AgxBadge
            type="colourfill"
            colour="grey"
            size="sm"
            text={totalCount?.toString() ?? '--'}
          />
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="vertical"
          fill
          contentAlignment="center"
          contentJustification="center"
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxTable
            variant="default"
            tableStyle="rounded"
            columns={tableColumns}
            rows={rows}
            onRowClick={onTaskRowClicked}
            showLoadingState={searchAllTasksPending && tasks.length === 0}
            noDataGlyphType="checklistSmile"
            noDataLabel="No task found."
          />
        </AgxSpacer>
      }
      setIsDialogOpen={setIsModalOpen}
      isOpen={isModalOpen}
    />
  );
};

export default TasksModal;
